import React, {useState, useEffect, useRef, useMemo} from 'react';
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dropdown} from "primereact/dropdown";
import axios from "axios";
import {AutoComplete} from "primereact/autocomplete";
import {Button} from "primereact/button";
import {Panel} from "primereact/panel";
import {InputText} from "primereact/inputtext";
import {InputMask} from "primereact/inputmask";
import {Calendar} from "primereact/calendar";
import moment from "moment";

const InventoryMovementListComponent = (props) => {

  const {t, detailedSearchVisible, onDetailedSearchActiveChange} = props;
  const [inventoryMovements, setInventoryMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rows, setRows] = useState(Number(process.env.REACT_APP_LIST_ROW_DEFAULT));
  const LIST_STORAGE_KEY = 'lastInventoryMovementListState';
  const DS_STORAGE_KEY = 'lastInventoryMovementDetailedSearchForm';

  const savedListState = localStorage.getItem(LIST_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(LIST_STORAGE_KEY))
    : null;

  const savedDetailedForm = localStorage.getItem(DS_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(DS_STORAGE_KEY))
    : null;

  const [detailedSearchQuery, setDetailedSearchQuery] = useState(
    savedListState?.detailedSearchQuery ?? ''
  );

  const [dsCreatedByName, setDsCreatedByName] = useState(savedDetailedForm?.dsCreatedByName ?? '');
  const [dsCreatedAtFrom, setDsCreatedAtFrom] = useState(savedDetailedForm?.dsCreatedAtFrom ?? '');
  const [dsCreatedAtTo, setDsCreatedAtTo] = useState(savedDetailedForm?.dsCreatedAtTo ?? '');
  const [dsMaterialName, setDsMaterialName] = useState(savedDetailedForm?.dsMaterialName ?? '');
  const [dsManufacturerName, setDsManufacturerName] = useState(savedDetailedForm?.dsManufacturerName ?? '');
  const [dsWarehouseFromId, setDsWarehouseFromId] = useState(savedDetailedForm?.dsWarehouseFromId ?? null);
  const [dsWarehouseLocationFromCode, setDsWarehouseLocationFromCode] = useState(savedDetailedForm?.dsWarehouseLocationFromCode ?? '');
  const [dsWarehouseToId, setDsWarehouseToId] = useState(savedDetailedForm?.dsWarehouseToId ?? null);
  const [warehouses, setWarehouses] = useState([]);
  const [dsWarehouseLocationToCode, setDsWarehouseLocationToCode] = useState(savedDetailedForm?.dsWarehouseLocationToCode ?? '');
  const [dsMovementType, setDsMovementType] = useState(savedDetailedForm?.dsMovementType ?? null);
  const [dsQuantity, setDsQuantity] = useState(savedDetailedForm?.dsQuantity ?? '');
  const [dsUnitId, setDsUnitId] = useState(savedDetailedForm?.dsUnitId ?? null);
  const [units, setUnits] = useState([]);

  const isDetailedSearchActive = React.useMemo(() => {
    return (detailedSearchQuery || '').toString().trim().length > 0;
  }, [detailedSearchQuery]);

  const [movementTypes, setMovementTypes] = useState([]);
  const [movementTypeMap, setMovementTypeMap] = useState({});

  const completeMaterials = (e) => {
  const q = e.query || '';
  apiCall('get', `/api/v2/material?search[name]=${encodeURIComponent(q)}&rows=10`)
    .catch(err => window.App.toastShow('error', t('error'), err.toString(), '', err.response));
};

  const dt = useRef(null);
  const [lazyParams, setLazyParams] = useState(() => {
    const savedLazyParams = savedListState?.lazyParams;
    if (savedLazyParams) {
      return {
        ...savedLazyParams,
        first: savedLazyParams.first ?? 0,
        rows: savedLazyParams.rows ?? rows,
        page: savedLazyParams.page ?? 0,
        sortField: savedLazyParams.sortField || 'id',
        sortOrder: typeof savedLazyParams.sortOrder === 'number' ? savedLazyParams.sortOrder : -1,
      };
    }
    return {
      first: 0,
      rows: rows,
      page: 0,
      sortField: 'id',
      sortOrder: -1,
    };
  });

  const movementTypeFilterTemplate = (options) => (
      <Dropdown
        value={options.value ?? null}
        options={movementTypes}
        optionLabel="label"
        optionValue="value"
        showClear
        placeholder={t('dsAll')}
        className="p-column-filter"
        onChange={(e) => options.filterApplyCallback(e.value ?? null)}
        style={{ width: '100%' }}
      />
    );

  const movementLabel = (val) => {
    if (val == null) return "";
    if (typeof val === "object") {
      const candidates = [ val.key ]
        .filter(Boolean)
        .map(String);
      for (const c of candidates) {
        if (
          movementTypeMap[c] ||
          movementTypeMap[c.toUpperCase?.()] ||
          movementTypeMap[c.toLowerCase?.()]
        ) {
          return (
            movementTypeMap[c] ||
            movementTypeMap[c.toUpperCase?.()] ||
            movementTypeMap[c.toLowerCase?.()]
          );
        }
      }
      return candidates[0] ?? "";
    }
    const k = String(val);
    return (
      movementTypeMap[k] ||
      movementTypeMap[k.toUpperCase?.()] ||
      movementTypeMap[k.toLowerCase?.()] ||
      k
    );
  };

  const persistDetailedForm = () => {
    const payload = {
      dsCreatedByName,
      dsMaterialName,
      dsManufacturerName,
      dsCreatedAtFrom,
      dsCreatedAtTo,
      dsWarehouseFromId,
      dsWarehouseToId,
      dsWarehouseLocationFromCode,
      dsWarehouseLocationToCode,
      dsMovementType,
      dsQuantity,
      dsUnitId,
    };

    localStorage.setItem(DS_STORAGE_KEY, JSON.stringify(payload));
  };

  const clearDetailedFormPersist = () => {
    localStorage.removeItem(DS_STORAGE_KEY);
  };

  const buildDetailedSearchQuery = () => {
    let parts = [];

    const like = (field, val) => {
      const v = (val || '').toString().trim();
      if (!v) return;
      parts.push(`search[${field}][operator]=like&search[${field}][value]=*${encodeURIComponent(v)}*`);
    };

    const createdAt = buildDateSearchParams('created_at', dsCreatedAtFrom, dsCreatedAtTo);
    if (createdAt) parts.push(createdAt);

    const equals = (field, val) => {
      if (val === '' || val === null || val === undefined) return;
      parts.push(`search[${field}][operator]==&search[${field}][value]=${encodeURIComponent(val)}`);
    };

    like('created_by.name', dsCreatedByName);
    like('material.name', dsMaterialName);
    like('material.manufacturer.name', dsManufacturerName);
    like('warehouse_location_from.code', dsWarehouseLocationFromCode);
    like('warehouse_location_to.code', dsWarehouseLocationToCode);

    equals('warehouse_from', dsWarehouseFromId);
    equals('warehouse_to', dsWarehouseToId);
    equals('movement_type', dsMovementType);
    like('quantity', dsQuantity);
    equals('unit', dsUnitId);

    return parts.filter(Boolean).join('&');
  };

  const parseCalendarRangeValue = (from, to) => {
    const values = [];
    const fromMoment = from ? moment(from, 'YYYY-MM-DD', true) : null;
    const toMoment = to ? moment(to, 'YYYY-MM-DD', true) : null;
    if (fromMoment && fromMoment.isValid()) {
      values.push(fromMoment.toDate());
    }
    if (toMoment && toMoment.isValid()) {
      values.push(toMoment.toDate());
    }
    return values.length ? values : null;
  };

  const handleCalendarRangeChange = (range) => {
    const [from, to] = range || [];
    if (!from) {
      setDsCreatedAtFrom('');
      setDsCreatedAtTo('');
      return;
    }
    setDsCreatedAtFrom(moment(from).format('YYYY-MM-DD'));
    setDsCreatedAtTo(to ? moment(to).format('YYYY-MM-DD') : '');
  };

  const buildDateSearchParams = (field, from, to) => {
    if (from && to) {
      return `search[${field}][operator]=between&search[${field}][value]=${from} 00:00:00,${to} 23:59:59`;
    }
    if (from && !to) {
      return `search[${field}][operator]=>=&search[${field}][value][]=${from} 00:00:00`;
    }
    if (!from && to) {
      return `search[${field}][operator]=<=&search[${field}][value][]=${to} 23:59:59`;
    }
    return '';
  };

  const doDetailedSearch = () => {
    const q = buildDetailedSearchQuery();
    setDetailedSearchQuery(q);
    persistDetailedForm();
    setLazyParams(prev => ({
      ...prev,
      first: 0,
      page: 0,
    }));
  };


  let loadLazyTimeout = null;
  const loadLazyData = () => {
    setLoading(true);
    if (loadLazyTimeout) {
      clearTimeout(loadLazyTimeout);
    }
    loadLazyTimeout = setTimeout(() => {
      let query = window.App.transformQueryParams(lazyParams)
      if (detailedSearchQuery) {
        if (!query || query.toString().length === 0) {
          query = '?' + detailedSearchQuery;
        } else if (query.toString().includes('?')) {
          query = query + (query.toString().endsWith('?') || query.toString().endsWith('&') ? '' : '&') + detailedSearchQuery;
        } else {
          query = '?' + query + '&' + detailedSearchQuery;
        }
      }

      apiCall('get', '/api/v2/inventory-movement' + query)
        .then(response => {
          setTotalRecords(response.data.data.total_item_count);
          setInventoryMovements(response.data.data.items)
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
          setLoading(false);
        })
    }, Math.random() * 1000 + 250);
  }
  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onFilter = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    _lazyParams['first'] = 0;
    _lazyParams['page'] = 0;
    setLazyParams(_lazyParams);
  }
  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: Number(process.env.REACT_APP_LIST_ROW_OP1), value: Number(process.env.REACT_APP_LIST_ROW_OP1)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP2), value: Number(process.env.REACT_APP_LIST_ROW_OP2)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP3), value: Number(process.env.REACT_APP_LIST_ROW_OP3)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP4), value: Number(process.env.REACT_APP_LIST_ROW_OP4)},
      ];

      return (
        <>
          {/*<span className="p-mx-1" style={{color: 'var(--text-color)', userSelect: 'none'}}>Tételek egy oldalon: </span>*/}
          <Dropdown value={options.value} options={dropdownOptions} onChange={(e) => setRows(e.target.value)}
                    appendTo={document.body}/>
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', userSelect: 'none', width: '120px', textAlign: 'center'}}>
                        {options.first} - {options.last} / {options.totalRecords}
        </span>
      )
    }
  };

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
  }

  useEffect(() => {
    apiCall('get', '/api/v2/warehouse?rows=1000')
      .then(response => {
        const data = response?.data?.data?.items || response?.data?.data || response?.data || [];
        setWarehouses(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
    apiCall('get', '/api/v2/unit?rows=1000')
      .then(response => {
        const data = response?.data?.data?.items || response?.data?.data || response?.data || [];
        setUnits(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, [t]);

  useEffect(() => {
    localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify({
      rows,
      lazyParams,
      detailedSearchQuery,
    }));
  }, [rows, lazyParams, detailedSearchQuery]);

  useEffect(() => {
    if (typeof onDetailedSearchActiveChange === 'function') {
      onDetailedSearchActiveChange(isDetailedSearchActive);
    }
  }, [isDetailedSearchActive, onDetailedSearchActiveChange]);

  useEffect(() => {
    apiCall('get', '/api/v2/dictionary/inventory-movement')
      .then(res => {
        const raw = res?.data?.data?.items || res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(raw) ? raw : []).map(x => {
          const code = x.code ?? x.value ?? x.key ?? x.id ?? '';
          const label = x.label ?? x.name ?? String(code);
          return { value: String(code), label, _raw: x };
        });

        const map = {};
        normalized.forEach(it => {
          const keys = [it._raw?.key].filter(Boolean).map(String);
          keys.forEach(k => {
            map[k] = it.label;
            map[k.toUpperCase?.()] = it.label;
            map[k.toLowerCase?.()] = it.label;
          });
        });

        setMovementTypes(normalized);
        setMovementTypeMap(map);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, [t]);

  useEffect(() => {
    loadLazyData();
  }, [lazyParams, detailedSearchQuery, window.App.state.rerenderInventoryMovementList])

  return (
    <>
    {detailedSearchVisible &&
      <Panel
        className="p-mb-2"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            doDetailedSearch();
          }
        }}
      >
        <div className="p-grid">

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnUser')}</label>
              <InputText value={dsCreatedByName} onChange={(e) => setDsCreatedByName(e.target.value)} />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnDateAndTime')}</label>
              <Calendar
                value={parseCalendarRangeValue(dsCreatedAtFrom, dsCreatedAtTo)}
                onChange={(e) => handleCalendarRangeChange(e.value)}
                selectionMode="range"
                readOnlyInput
                dateFormat="yy-mm-dd"
                showIcon
                showButtonBar
                placeholder={t('dsFromTo')}
                appendTo={document.body}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnMaterial')}</label>
              <InputText value={dsMaterialName} onChange={(e) => setDsMaterialName(e.target.value)} />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnManufacturer')}</label>
              <InputText value={dsManufacturerName} onChange={(e) => setDsManufacturerName(e.target.value)} />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnSourceWarehouse')}</label>
              <Dropdown
                value={dsWarehouseFromId}
                options={warehouses}
                optionLabel="name"
                optionValue="id"
                showClear
                filter
                placeholder={t('dsSelect')}
                onChange={(e) => setDsWarehouseFromId(e.value)}
              />
            </div>
          </div>

          {/* <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnSourceWarehouseLocation')}</label>
              <InputText value={dsWarehouseLocationFromCode} onChange={(e) => setDsWarehouseLocationFromCode(e.target.value)} />
            </div>
          </div> */}

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnDestinationWarehouse')}</label>
              <Dropdown
                value={dsWarehouseToId}
                options={warehouses}
                optionLabel="name"
                optionValue="id"
                showClear
                filter
                placeholder={t('dsSelect')}
                onChange={(e) => setDsWarehouseToId(e.value)}
              />
            </div>
          </div>

          {/* <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnDestinationWarehouseLocation')}</label>
              <InputText value={dsWarehouseLocationToCode} onChange={(e) => setDsWarehouseLocationToCode(e.target.value)} />
            </div>
          </div> */}

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnOperation')}</label>
              <Dropdown
                value={dsMovementType}
                options={movementTypes}
                optionLabel="label"
                optionValue="value"
                showClear
                placeholder={t('dsSelect')}
                onChange={(e) => setDsMovementType(e.value)}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnQuantity')}</label>
              <InputText value={dsQuantity} onChange={(e) => setDsQuantity(e.target.value)} />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnUnitOfMeasure')}</label>
              <Dropdown
                value={dsUnitId}
                options={units}
                optionLabel="name"
                optionValue="id"
                showClear
                filter
                placeholder={t('dsSelect')}
                onChange={(e) => setDsUnitId(e.value)}
              />
            </div>
          </div>

          <div className="p-col-12 p-text-right">
            <Button
              type="button"
              label={t('dsClearCriteria')}
              icon="pi pi-times"
              className="p-button-danger p-mr-2"
              onClick={() => {
                setDsCreatedByName('');
                setDsMaterialName('');
                setDsManufacturerName('');
                setDsWarehouseLocationFromCode('');
                setDsCreatedAtFrom('');
                setDsCreatedAtTo('');
                setDsWarehouseFromId(null);
                setDsWarehouseToId(null);
                setDsWarehouseLocationToCode('');
                setDsMovementType(null);
                setDsQuantity('');
                setDsUnitId(null);
                setDetailedSearchQuery('');
                clearDetailedFormPersist();
                localStorage.removeItem(LIST_STORAGE_KEY);
                setLazyParams({
                  first: 0,
                  rows: rows,
                  page: 0,
                  sortField: 'id',
                  sortOrder: -1,
                });
              }}
            />

            <Button
              type="button"
              label={t('dsSearch')}
              icon="pi pi-search"
              className="p-button-primary"
              onClick={doDetailedSearch}
            />
          </div>

        </div>
      </Panel>
      }

      <DataTable size={localStorage.getItem('scale')}
                 emptyMessage={t('noResults')}
                 value={inventoryMovements} paginator first={lazyParams.first} totalRecords={totalRecords} onPage={onPage}
                 onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                 onFilter={onFilter} filters={lazyParams.filters} loading={loading} lazy
                 paginatorTemplate={pager} rows={rows}
                 autoLayout={true}
                 responsiveLayout="scroll"
                 stripedRows
                 ref={dt}
                 dataKey="id"
      >

        <Column field="created_by.name" header={t('columnUser')} bodyClassName="col-body-font-bold" sortable body={(row) => row?.created_by?.name} />
        <Column field="created_at" header={t('columnDateAndTime')} sortable body={(row) => row?.created_at} />
        <Column field="material.name" header={t('columnMaterial')} bodyClassName="col-body-font-bold" body={(row) => row?.material?.name} />
        <Column field="warehouse_from.name" sortable filterField="warehouse_from.name" header={t('columnSourceWarehouse')} body={(row) => row?.warehouse_from?.name} />
        <Column field="warehouse_location_from.code" header={t('columnSourceWarehouseLocation')} sortable body={(row) => row?.warehouse_location_from?.code} />
        <Column field="warehouse_to.name" sortable header={t('columnDestinationWarehouse')} body={(row) => row?.warehouse_to?.name} />
        <Column field="warehouse_location_to.code" sortable header={t('columnDestinationWarehouseLocation')} body={(row) => row?.warehouse_location_to?.code} />

        <Column field="movement_type" header={t('columnOperation')} sortable showFilterMenu={false} filterElement={movementTypeFilterTemplate} body={(row) => movementLabel(row?.movement_type)} />

        <Column field="quantity" header={t('columnQuantity')} sortable body={(row) => row?.quantity} />
        <Column field="unit.name" header={t('columnUnitOfMeasure')} sortable body={(row) => row?.unit?.name} />


      </DataTable>
    </>
  )
}
export default withTranslation('common')(InventoryMovementListComponent);
